import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert } from "@mui/material";

/**
 * mode "add":  new task on the plan.
 * mode "note": edit the note on an existing task. Only the latest note lives on
 *              the task itself; every change is also written to the plan's
 *              history with its full text, so earlier notes are never lost.
 */
export default function CarePlanTaskDialog({ open, mode, task, staff, saving, error, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("UNASSIGNED");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    setTitle("");
    setDescription("");
    setAssignee("UNASSIGNED");
    setDueDate("");
    setNotes(mode === "note" && task ? task.notes || "" : "");
  }, [open, mode, task]);

  const handleSave = () => {
    if (mode === "note") {
      onSave({ notes });
      return;
    }
    if (!title.trim()) {
      setLocalError("Task title is required.");
      return;
    }
    let assigneeType = "PROVIDER";
    let assigneeProviderId = null;
    if (assignee === "PATIENT") assigneeType = "PATIENT";
    else if (assignee.startsWith("S:")) assigneeProviderId = Number(assignee.slice(2));
    onSave({ title: title.trim(), description: description.trim() || null, assigneeType, assigneeProviderId, dueDate: dueDate || null });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "note" ? `Note: ${task?.title || ""}` : "Add task"}</DialogTitle>
      <DialogContent dividers>
        {(localError || error) && <Alert severity="error" sx={{ mb: 2 }}>{localError || error}</Alert>}
        {mode === "note" ? (
          <TextField
            label="Note" multiline minRows={3} fullWidth autoFocus value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="Saved on the task, and recorded in the plan history."
          />
        ) : (
          <>
            <TextField label="Task" required fullWidth autoFocus value={title} sx={{ mb: 2 }}
              inputProps={{ maxLength: 300 }} onChange={(e) => setTitle(e.target.value)} />
            <TextField label="Details" multiline minRows={2} fullWidth value={description} sx={{ mb: 2 }}
              onChange={(e) => setDescription(e.target.value)} />
            <TextField select label="Assigned to" fullWidth value={assignee} sx={{ mb: 2 }}
              onChange={(e) => setAssignee(e.target.value)}
              helperText="Tasks assigned to the patient appear for them to complete.">
              <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
              <MenuItem value="PATIENT">The patient</MenuItem>
              {staff.map((s) => <MenuItem key={s.id} value={`S:${s.id}`}>{s.name}</MenuItem>)}
            </TextField>
            <TextField label="Due date" type="date" fullWidth value={dueDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setDueDate(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : mode === "note" ? "Save note" : "Add task"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}